import { useUserStore } from '@talon/web/src/context';
import { Anchor, Box, Button, Footer, Grid, Header, Text } from 'grommet';
import TalonLogo from 'jsx:@talon/web/public/images/talon-ai-logo.svg';
import { observer } from 'mobx-react-lite';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FooterNav } from '../nav/FooterNav';
import { UserMenu } from '../nav/UserMenu';
import { ImpersonationBanner } from '../ui';

/**
 * A separate, minimal shell for /admin — deliberately does not reuse HeaderNav, which carries
 * the entire producer app's chrome (project selector, Pre-Pro/Production/Post-Production
 * mega-menu, quick estimator). None of that belongs on an internal back-office screen; admin
 * staff need the logo, a way to get to their own user menu, and nothing else competing for
 * space with AdminMenu's sidebar.
 */
export const AdminLayout = observer(({ children }: { children: ReactNode }) => {
  const userStore = useUserStore();
  const navigate = useNavigate();

  return (
    <Grid
      height={{ min: '100vh', max: '100vh' }}
      rows={['auto', 'auto', 'flex', 'auto']}
      columns={['auto', 'flex']}
      areas={[
        { name: 'header', start: [0, 0], end: [1, 0] },
        { name: 'banner', start: [0, 1], end: [1, 1] },
        { name: 'main', start: [0, 2], end: [1, 2] },
        { name: 'footer', start: [0, 3], end: [1, 3] },
      ]}
      gap="none"
    >
      <Header
        gridArea="header"
        background={{ color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)' }}
        pad={{ horizontal: 'medium' }}
        height="72px"
        direction="row"
        justify="between"
        align="center"
      >
        <Anchor as={Button} onClick={() => navigate('/admin')}>
          <Box direction="row" gap="small" align="center">
            <TalonLogo width="32" height="32" />
            <Text weight={600} color="white" style={{ letterSpacing: '0.1rem' }}>
              TALON
            </Text>
            <Box background="white" round="999px" pad={{ horizontal: '10px', vertical: '3px' }}>
              <Text color="#0d48a1" size="12px" weight={600}>Admin</Text>
            </Box>
          </Box>
        </Anchor>
        {userStore.user?.id && <UserMenu />}
      </Header>
      <Box gridArea="banner">
        <ImpersonationBanner />
      </Box>
      <Box gridArea="main" background="background-back" overflow="auto" pad={{ bottom: '64px' }}>
        {children}
      </Box>
      <Footer gridArea="footer">
        <FooterNav />
      </Footer>
    </Grid>
  );
});
